import { useMemo } from 'react'
import { Link, useSearch } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { SMITH_KERNS_WARNING, dailyWeather, degreeDaysSince, dollarSpot, dayOfYear, runResponse, type RunResponse } from '@gk/contracts'
import { useClub, useEngineState } from '../club/ClubProvider.tsx'
import { useNotebook } from '../data/measure.ts'
import { siteAltitude, useWater, type ProfileRow } from '../data/water.ts'
import { WATER_METHODS, adviceForAreas, meterUse, siteSettings, weatherRows, type AreaAdvice } from '../data/waterUi.ts'
import { fmtNum } from '../lib/format.ts'
import { formatLocal, localDate } from '../lib/time.ts'
import { BackLink, Banner, Pill, Section } from '../ui/components.tsx'
import { Picto } from '../ui/pictos.tsx'
import { VerdictPill, useAreaOptions } from './measures/shared.tsx'
import { FaultsTab } from './water/Faults.tsx'
import { ProfileTab } from './water/Profile.tsx'
import { RunsTab } from './water/Runs.tsx'
import { ConstraintsNote, useWaterTab, WATER_TABS } from './water/shared.tsx'
// M24-W: the forecast's own block ("Prévision, à part")
import { WATER_FORECAST_DAYS, aggText, chanceText, coversFuture, decimalsOf, forecastAge, forecastDayLabel, forecastDays, unitOf, useForecast } from '../data/weatherForecast.ts'
import { NoFutureLine } from '../ui/ForecastNoFuture.tsx'
import { ForecastSource } from '../ui/ForecastSource.tsx'
import { RowLink } from '../ui/RowLink.tsx'
import { useNow } from '../ui/useNow.ts'
import { Et0Value, distanceText } from './Forecast.tsx'

/** Water and weather (M14, ADR-102, ADR-103). It RECORDS what was irrigated and what went wrong, and ADVISES from what was measured; it never sends anything to the irrigation system. Every figure shows what it was made of,
 *  and where an input is missing it says which, never a default. The club's own parameters are the club's: nothing is seeded.
 *  M23 item 46: the page opens on the tab the link asks for (`?tab=runs`, `?tab=faults&new=1` from Today's "Terrain" row, the router's keys), and on the runs for
 *  whoever does not manage the water (the crew's acts); the six tabs of the profile are unchanged.
 *  M24-W: under the advice cards, the forecast's own block "Prévision, à part" (forecast.read), which no calculation of this page reads. */
export function WaterPage() {
  const { t } = useTranslation()
  const { can } = useClub()
  const search = useSearch({ strict: false }) as { tab?: string; new?: string }
  const canManage = can('water.manage')
  // the default follows the link and the permission as they come (a fresh device knows its permissions only after its first pull); a tap holds until the next link
  const { tab, choose } = useWaterTab(search, canManage)
  if (!can('water.read')) return <section className="flex flex-col gap-4" data-testid="water-page"><h1 className="text-2xl font-bold">{t('water.title')}</h1><Banner tone="warn"><span data-testid="water-denied">{t('water.denied')}</span></Banner></section>
  return (
    <section className="flex flex-col gap-4" data-testid="water-page">
      <BackLink to="/settings">← {t('common.back')}</BackLink>
      <h1 className="text-2xl font-bold">{t('water.title')}</h1>
      <Banner tone="info"><span data-testid="water-boundary">{t('water.boundary')}</span></Banner>
      {/* M16-L1: the site's constraints (pressure windows, pump limits, the fontainier's rules), read by people, applied by nothing; on every tab */}
      <ConstraintsNote />
      <div role="tablist" aria-label={t('water.title')} className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        {WATER_TABS.map((id) => (
          <button key={id} role="tab" aria-selected={tab === id} className={`btn !px-2 ${tab === id ? 'btn-primary' : ''}`} onClick={() => choose(id)} data-testid={`water-tab-${id}`}>{t(`water.tabs.${id}`)}</button>
        ))}
      </div>
      {tab === 'advice' && <AdviceTab />}
      {tab === 'runs' && <RunsTab />}
      {tab === 'faults' && <FaultsTab autoNew={!!search.new} />}
      {tab === 'settings' && <ProfileTab />}
    </section>
  )
}

function Num({ n, unit, digits = 1 }: { n: number | null; unit: string; digits?: number }) {
  const { i18n } = useTranslation()
  return <>{n === null ? '—' : `${fmtNum(n, i18n.language, digits)} ${unit}`}</>
}

// ---------------------------------------------------------------------------------------------------- the advice
function AdviceTab() {
  const { t, i18n } = useTranslation()
  const { site } = useClub()
  const nb = useNotebook()
  const water = useWater()
  const areas = useAreaOptions()
  const today = localDate(new Date(), site.timezone)
  const settings = useMemo(() => siteSettings(water.profiles), [water.profiles])
  const weather = useMemo(() => dailyWeather(nb.readings), [nb.readings])
  const rows = useMemo(() => weatherRows(weather, settings.latitude, today, 14), [weather, settings.latitude, today])
  const spot = useMemo(() => dollarSpot(weather, today), [weather, today])
  const gdd = useMemo(() => (settings.gddBase === null ? null : degreeDaysSince(weather, `${today.slice(0, 4)}-01-01`, today, settings.gddBase)), [weather, settings.gddBase, today])
  const advice = useMemo(() => (areas ? adviceForAreas({ areas, readings: nb.readings, targets: nb.targets, profiles: water.profiles, runs: water.runs, today }) : []), [areas, nb.readings, nb.targets, water.profiles, water.runs, today])
  const use = useMemo(() => meterUse(nb.readings).slice(-8).reverse(), [nb.readings])
  // M16-M: what the probe read before and after each run on the area (the last five), with the club's root zone for the millimetres when it gave one
  const responses = useMemo(() => new Map(advice.map((a) => [a.area.id, runResponse(water.runs.filter((r) => r.area_id === a.area.id), nb.readings, { rootZoneMm: a.profile?.root_zone_mm ?? null }).slice(0, 5)])), [advice, water.runs, nb.readings])
  if (!nb.ready || !water.ready || !areas) return <p>{t('common.loading')}</p>
  void dayOfYear
  return (
    <div className="flex flex-col gap-5" data-testid="water-advice">
      <Section title={t('water.weather.title')} help={t('water.weather.help')} testId="water-weather">
        <div className="card overflow-x-auto">
          <table className="w-full text-sm" data-testid="weather-table">
            <thead><tr className="text-left opacity-70"><th>{t('water.weather.date')}</th><th>{t('water.weather.tmax')}</th><th>{t('water.weather.tmin')}</th><th>{t('water.weather.rh')}</th><th>{t('water.weather.rain')}</th><th>ET0</th></tr></thead>
            {/* M23 item 48: a day without an observed reading reads at 70 % (3.4:1 at 50 %) and says "estimé" beside its date (13 px, the audit's floor) */}
            <tbody>
              {rows.map((w) => (
                <tr key={w.date} data-testid="weather-row" data-date={w.date} data-observed={w.observed ? 'true' : 'false'} className={w.observed ? '' : 'opacity-70'}>
                  <td>{w.date.slice(5).split('-').reverse().join('/')}{!w.observed && <> <i className="text-[13px]">{t('water.weather.estimated')}</i></>}</td>
                  <td><Num n={w.tmax} unit="°C" /></td><td><Num n={w.tmin} unit="°C" /></td><td><Num n={w.rh} unit="%" digits={0} /></td><td><Num n={w.rain} unit="mm" /></td><td data-testid="weather-et0"><Num n={w.et0} unit="mm" /></td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="meta mt-2">{t('water.weather.note')}</p>
          {settings.latitude === null && <p className="mt-1 text-sm font-semibold" data-testid="weather-no-latitude">{t('water.missing.latitude')}</p>}
        </div>
        {/* M16-M: the timeline (rain, temperatures, humidity, soil moisture, the meter, the runs, the observations on one axis); its route lands with Z's close (docs/m16-requests/m.md) */}
        <Link to="/measures/timeline" className="btn" data-testid="water-to-timeline"><Picto name="measure" className="size-5" />{t('water.toTimeline')}</Link>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="card flex flex-col gap-1" data-testid="dollar-spot">
            <h3 className="font-bold">{t('water.spot.title')}</h3>
            {spot ? (
              <>
                <p className="text-2xl font-bold" data-testid="spot-value">{fmtNum(spot.probability * 100, i18n.language, 0)} %</p>
                <p><Pill tone={spot.probability >= SMITH_KERNS_WARNING ? 'amber' : 'green'}><span data-testid="spot-level">{t(spot.probability >= SMITH_KERNS_WARNING ? 'water.spot.high' : 'water.spot.low')}</span></Pill></p>
                <p className="meta">{t('water.spot.inputs', { t: fmtNum(spot.temp5, i18n.language, 1), rh: fmtNum(spot.rh5, i18n.language, 0), date: spot.date.split('-').reverse().join('/') })}</p>
              </>
            ) : <p className="meta" data-testid="spot-missing">{t('water.spot.missing')}</p>}
            <p className="meta">{t('water.spot.note')}</p>
          </div>
          <div className="card flex flex-col gap-1" data-testid="gdd-card">
            <h3 className="font-bold">{t('water.gdd.title')}</h3>
            {gdd ? (
              <>
                <p className="text-2xl font-bold" data-testid="gdd-value">{fmtNum(gdd.sum, i18n.language, 0)} °C·j</p>
                <p className="meta">{t('water.gdd.inputs', { base: fmtNum(settings.gddBase!, i18n.language, 1), count: gdd.counted, days: gdd.days })}</p>
              </>
            ) : <p className="meta" data-testid="gdd-missing">{t('water.gdd.missing')}</p>}
          </div>
        </div>
      </Section>

      <Section title={t('water.soil.title')} help={t('water.soil.help')} testId="water-soil">
        {advice.length === 0 && <p className="opacity-70" data-testid="advice-none">{t('water.soil.none')}</p>}
        <ul className="flex flex-col gap-3">{advice.map((a) => <AdviceCard key={a.area.id} a={a} responses={responses.get(a.area.id) ?? []} />)}</ul>
      </Section>

      {/* M24-W: the forecast, apart: handed only the club's parameters (latitude, altitude), never a reading nor the ET0 of the advice above (rules 1 and 2) */}
      <WaterForecastBlock profiles={water.profiles} />

      <Section title={t('water.meter.title')} help={t('water.meter.help')} testId="water-meter">
        {use.length === 0 ? <p className="opacity-70" data-testid="meter-none">{t('water.meter.none')}</p> : (
          <ul className="flex flex-col gap-2" data-testid="meter-list">
            {use.map((u) => (
              <li key={`${u.from}${u.to}`} className="card flex flex-wrap items-baseline justify-between gap-2" data-testid="meter-row">
                <span>{u.from.split('-').reverse().join('/')} → {u.to.split('-').reverse().join('/')}</span>
                <span className="font-bold">{fmtNum(u.m3, i18n.language, 1)} m³ <span className="text-sm font-normal opacity-70">({fmtNum(u.perDay, i18n.language, 1)} m³/{t('water.meter.perDay')})</span></span>
              </li>
            ))}
          </ul>
        )}
        <Link to="/measures" className="btn" data-testid="water-to-notebook">{t('water.toNotebook')}</Link>
      </Section>

      <details className="card" data-testid="water-methods">
        <summary className="flex min-h-14 cursor-pointer items-center font-bold">{t('water.methods.title')}</summary>
        <ul className="mt-2 flex flex-col gap-2 text-sm">
          {/* the methods THIS page uses (M24 review: the Beaufort force is the forecast's, never printed here) */}
          {WATER_METHODS.map((m) => <li key={m.code} data-testid="water-method" data-code={m.code}><strong>{m.name}</strong> (v{m.version})<br /><span className="opacity-75">{m.source}</span></li>)}
        </ul>
        <p className="meta mt-2">{t('water.methods.note')}</p>
      </details>
    </div>
  )
}

function AdviceCard({ a, responses }: { a: AreaAdvice; responses: RunResponse[] }) {
  const { t, i18n } = useTranslation()
  const { site } = useClub()
  const x = a.advice
  const online = useEngineState().online !== false
  void online
  const sideText = (s: RunResponse['before']) => (s ? `${t('water.response.side', { value: fmtNum(s.value, i18n.language, 1), when: formatLocal(s.at, site.timezone), count: s.n })}${s.depthMm !== null ? ` · ${t('measures.atDepth', { mm: s.depthMm })}` : ''}${s.method ? ` · ${s.method}` : ''}` : t('water.response.nd'))
  const signed = (v: number, digits: number) => `${v > 0 ? '+' : ''}${fmtNum(v, i18n.language, digits)}`
  return (
    <li className="card flex flex-col gap-2" data-testid="advice-card" data-area={a.area.id}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <span className="font-semibold">{a.area.label}</span>
        {x.latest && <span className="text-xl font-bold" data-testid="advice-latest">{fmtNum(x.latest.mean, i18n.language, 1)} %</span>}
      </div>
      {x.latest && (
        <p className="flex flex-wrap items-center gap-2 text-sm opacity-90">
          <span>{t('water.soil.latest', { date: x.latest.date.split('-').reverse().join('/'), count: x.latest.n, age: x.latest.ageDays })}</span>
          <span data-testid="advice-verdict"><VerdictPill verdict={x.verdict} target={a.target ?? undefined} unit="pct" /></span>
        </p>
      )}
      <dl className="grid grid-cols-[1fr_auto] gap-x-4 gap-y-1 text-sm">
        <dt className="opacity-70">{t('water.soil.etc')}</dt><dd data-testid="advice-etc">{x.etc !== null && x.et0 ? `${fmtNum(x.etc, i18n.language, 1)} mm/${t('water.meter.perDay')} (ET0 ${fmtNum(x.et0.mean, i18n.language, 1)} × Kc ${fmtNum(a.profile!.kc!, i18n.language, 2)}, ${x.et0.days} ${t('water.soil.days')})` : '—'}</dd>
        <dt className="opacity-70">{t('water.soil.toMiddle')}</dt><dd data-testid="advice-to-middle">{x.toMiddle === null ? '—' : x.toMiddle === 0 ? t('water.soil.alreadyThere') : `${fmtNum(x.toMiddle, i18n.language, 1)} mm`}</dd>
        <dt className="opacity-70">{t('water.soil.daysToLow')}</dt><dd data-testid="advice-days-low">{x.daysToLow === null ? '—' : x.daysToLow === 0 ? t('water.soil.alreadyThere') : t('water.soil.daysValue', { n: fmtNum(x.daysToLow, i18n.language, 1) })}</dd>
        <dt className="opacity-70">{t('water.soil.rain7')}</dt><dd data-testid="advice-rain">{x.rain7 ? `${fmtNum(x.rain7.mm, i18n.language, 1)} mm (${x.rain7.days} ${t('water.soil.days')})` : '—'}</dd>
        <dt className="opacity-70">{t('water.soil.irrigation7')}</dt>
        <dd data-testid="advice-irrigation">{x.irrigation7.counted + x.irrigation7.withoutDepth + x.irrigation7.tests === 0 ? t('water.soil.noRun') : `${fmtNum(x.irrigation7.depthMm, i18n.language, 1)} mm (${t('water.soil.runsCounted', { count: x.irrigation7.counted })}${x.irrigation7.withoutDepth ? `, ${t('water.soil.runsUncounted', { count: x.irrigation7.withoutDepth })}` : ''}${x.irrigation7.tests ? `, ${t('water.soil.runsTests', { count: x.irrigation7.tests })}` : ''})`}</dd>
      </dl>
      {x.missing.length > 0 && (
        <div data-testid="advice-missing"><Banner tone="warn">
          <ul className="list-disc pl-5">{x.missing.map((m) => <li key={m} data-code={m}>{t(`water.missing.${m}`)}</li>)}</ul>
        </Banner></div>
      )}
      <p className="meta">{t('water.soil.projection')}</p>
      {/* M16-M (benchmark 28): the soil's response to the last five runs, observed by the probe before and after; never a cause */}
      <details data-testid="advice-responses" data-count={responses.length}>
        <summary className="flex min-h-14 cursor-pointer items-center font-semibold">{t('water.response.title')}</summary>
        <p className="meta">{t('water.response.help')}</p>
        {responses.length === 0 ? <p className="meta mt-2" data-testid="response-none">{t('water.response.none')}</p> : (
          <ul className="mt-2 flex flex-col gap-2">
            {responses.map((r) => (
              <li key={r.runId} className="rounded-lg border border-slate-200 p-2 text-sm dark:border-night-line" data-testid="response-row" data-run={r.runId}>
                <p className="font-semibold">{t('water.response.run', { date: r.date.split('-').reverse().join('/') })} <span className="font-normal">· {t(`water.runKind.${r.kind}`)}{r.durationMin !== null ? ` · ${fmtNum(r.durationMin, i18n.language, 1)} min` : ''}{r.depthMm !== null ? ` · ${fmtNum(r.depthMm, i18n.language, 2)} mm` : ''}</span></p>
                <p className="meta">{t('water.response.before')} : <span data-testid="response-before">{sideText(r.before)}</span></p>
                <p className="meta">{t('water.response.after')} : <span data-testid="response-after">{sideText(r.after)}</span></p>
                <p className="font-semibold" data-testid="response-delta">
                  {r.deltaPct === null ? t('water.response.nd') : t('water.response.delta', { pts: signed(r.deltaPct, 1) })}
                  {r.deltaPct !== null && <span className="font-normal"> ({r.deltaMm !== null ? t('water.response.deltaMm', { mm: signed(r.deltaMm, 1), root: fmtNum(a.profile!.root_zone_mm!, i18n.language, 0) }) : t('water.response.noRoot')})</span>}
                </p>
              </li>
            ))}
          </ul>
        )}
        <p className="meta mt-2">{t('water.response.observed')}</p>
      </details>
    </li>
  )
}

/** M24-W, Eau › Conseils (docs/M24.md "Screens"; rules 1, 2, 6; ADR-126): the forecast's own block, "Prévision, à part" — the rain and ET0 prévue of the next five
 *  club days (from tomorrow: today is never a complete day of the forecast), each under its source line, the sentence that it enters no calculation of this page,
 *  and ONE fixed line on the two ET0: the forecast's (FAO-56 Penman-Monteith on the provider's values for the connector's point, at its distance) and the
 *  advice's (Hargreaves-Samani on the club's thermometer) are another method and another place, and are not compared. The block is handed the club's parameters
 *  only — never a reading, never the advice's ET0 —, so no difference, ratio or arrow between the two can be drawn. forecast.read only, never the workstation;
 *  online (the forecast is on no device). */
export function WaterForecastBlock({ profiles }: { profiles: readonly ProfileRow[] }) {
  const { t, i18n } = useTranslation()
  const { site, can, kiosk } = useClub()
  const allowed = can('forecast.read') && !kiosk
  const read = useForecast(allowed)
  const now = useNow(60_000)
  const latitude = siteSettings(profiles).latitude, altitude = siteAltitude(profiles)
  const minute = Math.floor(now / 60_000)
  const today = localDate(minute * 60_000, site.timezone)
  const view = read.view
  const days = useMemo(() => (view ? forecastDays(view, { now: minute * 60_000, tz: site.timezone, latitude, altitude, days: WATER_FORECAST_DAYS + 1 }).filter((d) => d.date !== today).slice(0, WATER_FORECAST_DAYS) : []), [view, minute, site.timezone, latitude, altitude, today])
  if (!allowed) return null
  const lang = i18n.language
  return (
    <Section title={t('forecast.water.title')} testId="water-forecast">
      <p className="font-semibold" data-testid="water-forecast-sentence">{t('forecast.water.sentence')}</p>
      {view === undefined && <p className="meta" data-testid={!read.online || read.error === 'OFFLINE' ? 'water-forecast-offline' : 'water-forecast-loading'}>{!read.online || read.error === 'OFFLINE' ? t('forecast.needsNetwork') : t('forecast.loading')}</p>}
      {view === null && <p className="meta" data-testid="water-forecast-none">{t('forecast.none')}</p>}
      {/* M24 review: an issue that covers no hour to come is said so with its age; no empty list, no source line under nothing */}
      {view && !coversFuture(view, minute * 60_000) && <NoFutureLine view={view} ageMs={forecastAge(view, now, read.receivedAt).ms} stale={forecastAge(view, now, read.receivedAt).stale} testId="water-forecast-past" />}
      {view && coversFuture(view, minute * 60_000) && (
        <>
          <ul className="card flex flex-col divide-y divide-slate-200 !py-1 dark:divide-night-line">
            {days.map((d) => (
              <li key={d.date} className="flex flex-col gap-0.5 py-2" data-testid="water-forecast-day" data-date={d.date}>
                <span className="font-semibold first-letter:uppercase">{forecastDayLabel(d.date, lang, site.timezone)}</span>
                <span>{t('forecast.water.rain')} · {aggText(d.rain, unitOf(view, 'rain_1h'), decimalsOf(view, 'rain_1h'), lang, t)}{d.rain.kind === 'value' ? ` · ${chanceText(d.rainChance, lang, t)}` : ''}</span>
                <span>{t('forecast.water.et0')} · <Et0Value view={view} et0={d.et0} /></span>
              </li>
            ))}
          </ul>
          <ForecastSource view={view} mode="derived" />
          <ForecastSource view={view} mode="et0" />
          <p className="meta" data-testid="water-forecast-fixed">{t('forecast.water.fixed', { provider: view.source.provider_name, point: view.connector.point_label, distance: distanceText(view, lang, t) })}</p>
        </>
      )}
      <RowLink to="/forecast" icon="weather" tone="water" testId="water-forecast-open" title={t('forecast.card.open')} />
    </Section>
  )
}
